import type { ReactNode } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import classNames from 'classnames';
import styles from './fallbackProductDescription.module.scss';
import { useTranslation } from 'next-i18next';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';

type FallbackProductDescriptionProps = {
  materialName: string;
  productionMethod?: IbhProductionMethods;
};
export const FallbackProductDescription = ({
  materialName,
  productionMethod,
}: FallbackProductDescriptionProps): ReactNode => {
  const { t } = useTranslation(['common', 'product/productDetails']);

  if (!materialName) {
    return null;
  }

  const normalizeMaterialName =
    productionMethod === IbhProductionMethods.SEMIFINISHED ? materialName.replace(/-CNC/g, '') : materialName;

  const rawHtml = t(`${normalizeMaterialName}`, { ns: 'free-form-hub/materialsDescription', defaultValue: '' });

  const decodeHtmlEntities = (html: string): string => {
    return html
      .replace(/&lt;\s*/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'");
  };

  const decodedHtml = decodeHtmlEntities(rawHtml);

  const cleanHtml = DOMPurify.sanitize(decodedHtml, {
    FORBID_ATTR: ['style'],
    ALLOWED_TAGS: ['p', 'ul', 'ol', 'li', 'strong', 'em', 'br', 'span', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  });

  const fallbackText = t(`MATERIALS.${normalizeMaterialName}_DESCRIPTION`, { ns: 'free-form-hub/materials' });

  return (
    <div>
      {!cleanHtml.length && <span className="text-[16px] font-bold pb-2.5">{fallbackText}</span>}
      <div className={classNames(styles.productDescription)} dangerouslySetInnerHTML={{ __html: cleanHtml }} />
    </div>
  );
};
